import React from 'react'
import { useNavigate } from 'react-router-dom'
import { Header } from '../../components/layout/Header'
import { Card } from '../../components/common/Card'
import { useTheme } from '../../contexts/ThemeContext'

export const DataProtectionPage: React.FC = () => {
  const { colors } = useTheme()
  const navigate = useNavigate()

  const gdprRequirements = [
    {
      title: 'Lawful Basis for Processing',
      icon: '&#9989;',
      items: [
        'Consent: Freely given, specific, informed',
        'Contract: Necessary for order fulfillment',
        'Legal Obligation: Tax records, fraud prevention',
        'Legitimate Interest: Marketing (with opt-out)',
      ],
    },
    {
      title: 'Data Subject Rights',
      icon: '&#128100;',
      items: [
        'Right to access (respond within 30 days)',
        'Right to rectification',
        'Right to erasure ("right to be forgotten")',
        'Right to data portability',
        'Right to object to processing',
      ],
    },
    {
      title: 'Required Documentation',
      icon: '&#128196;',
      items: [
        'Privacy Policy (clear, accessible)',
        'Cookie Policy and consent mechanism',
        'Records of Processing Activities (ROPA)',
        'Data Processing Agreements with vendors',
        'Data Protection Impact Assessments',
      ],
    },
    {
      title: 'Security Measures',
      icon: '&#128274;',
      items: [
        'Encryption of personal data',
        'Access controls and authentication',
        'Regular security testing',
        'Incident response procedures',
        'Staff training on data protection',
      ],
    },
  ]

  const countrySpecific = [
    { country: 'Germany', requirement: 'DPA: BfDI | Strictest GDPR interpretation | Additional employee data rules' },
    { country: 'France', requirement: 'DPA: CNIL | Cookie consent banner requirements | Specific marketing rules' },
    { country: 'Italy', requirement: 'DPA: Garante | Marketing consent requirements | Retention period rules' },
    { country: 'Spain', requirement: 'DPA: AEPD | LOPDGDD supplements GDPR | Specific transparency requirements' },
    { country: 'Netherlands', requirement: 'DPA: AP | Cookie wall restrictions | Direct marketing opt-in required' },
  ]

  return (
    <div style={{ minHeight: '100vh', backgroundColor: colors.background }}>
      <Header />
      <main style={{ maxWidth: '1440px', margin: '0 auto', padding: '40px 32px' }}>
        <div style={{ marginBottom: '32px' }}>
          <button
            onClick={() => navigate('/compliance')}
            style={{
              background: 'none',
              border: 'none',
              color: colors.primary,
              cursor: 'pointer',
              marginBottom: '16px',
              fontSize: '14px',
            }}
          >
            &larr; Back to Compliance Center
          </button>
          <h1 style={{ fontSize: '32px', marginBottom: '8px', color: colors.textPrimary }}>
            Data Protection & GDPR
          </h1>
          <p style={{ color: colors.textSecondary, fontSize: '16px' }}>
            GDPR compliance requirements for selling to EU customers
          </p>
        </div>

        {/* GDPR Overview */}
        <Card style={{ marginBottom: '32px', borderLeft: '4px solid #7B68EE' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
            <span style={{ fontSize: '32px' }}>&#128274;</span>
            <div>
              <h3 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '8px', color: colors.textPrimary }}>
                GDPR Applies to UK E-commerce Sellers
              </h3>
              <p style={{ color: colors.textSecondary, lineHeight: 1.6 }}>
                Even after Brexit, GDPR applies to UK businesses that offer goods/services to EU residents or
                monitor their behavior. Fines can reach up to €20 million or 4% of global annual turnover.
                UK sellers need both UK GDPR and EU GDPR compliance.
              </p>
            </div>
          </div>
        </Card>

        {/* Requirements Grid */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '24px', marginBottom: '32px' }}>
          {gdprRequirements.map((req) => (
            <Card key={req.title}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                <span style={{ fontSize: '28px' }} dangerouslySetInnerHTML={{ __html: req.icon }} />
                <h3 style={{ fontSize: '18px', fontWeight: 600, color: colors.textPrimary }}>{req.title}</h3>
              </div>
              <ul style={{ margin: 0, paddingLeft: '16px', color: colors.textSecondary, fontSize: '14px', lineHeight: 1.8 }}>
                {req.items.map((item, idx) => (
                  <li key={idx}>{item}</li>
                ))}
              </ul>
            </Card>
          ))}
        </div>

        {/* Country-Specific Requirements */}
        <Card>
          <h2 style={{ fontSize: '20px', marginBottom: '20px', color: colors.textPrimary }}>
            Country-Specific Data Protection
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {countrySpecific.map((item) => (
              <div
                key={item.country}
                style={{
                  padding: '16px',
                  border: `1px solid ${colors.border}`,
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '16px',
                }}
              >
                <span style={{ fontWeight: 600, minWidth: '100px', color: colors.textPrimary }}>{item.country}</span>
                <span style={{ fontSize: '14px', color: colors.textSecondary }}>{item.requirement}</span>
              </div>
            ))}
          </div>
        </Card>
      </main>
    </div>
  )
}
